import { MessagesSquare } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, EmptyState, Spinner } from '@/components/ui'

import { useWebChatChannels } from '../queries'
import { CreateWebChatForm } from './CreateWebChatForm'
import { WebChatChannelCard } from './WebChatChannelCard'
import { WebChatCredentials } from './WebChatCredentials'

interface RevealedCredentials {
  readonly publishableKey: string
  readonly signingSecret: string
}

/** Lista e gerencia os widgets de WebChat do tenant, e revela as credenciais (uma vez) após criar ou rotacionar. */
export function WebChatChannelList({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation()
  const { data, isLoading, isError, refetch } = useWebChatChannels(tenantId)
  const [revealed, setRevealed] = useState<RevealedCredentials | null>(null)

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t('webchat.loading')} />
      </div>
    )
  }

  if (isError || data === undefined) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Alert variant="danger" title={t('webchat.loadError')}>
          {t('webchat.loadErrorHint')}
        </Alert>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => {
            void refetch()
          }}
        >
          {t('retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {revealed !== null && (
        <WebChatCredentials
          publishableKey={revealed.publishableKey}
          signingSecret={revealed.signingSecret}
          onDismiss={() => {
            setRevealed(null)
          }}
        />
      )}

      <CreateWebChatForm
        tenantId={tenantId}
        onCreated={(created) => {
          setRevealed({
            publishableKey: created.publishableKey,
            signingSecret: created.signingSecret,
          })
        }}
      />

      {data.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          title={t('webchat.empty.title')}
          description={t('webchat.empty.description')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          {data.map((channel) => (
            <WebChatChannelCard
              key={channel.id}
              tenantId={tenantId}
              channel={channel}
              onSecretRotated={(publishableKey, signingSecret) => {
                setRevealed({ publishableKey, signingSecret })
              }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
